import { generateSVG, normalizeFilename, parseFont } from './gen_svg.js';
import { ANIM_SPEED } from './constants/appConstants.js';
import {
  fetchGenerations,
  createGenerationRecord,
  createEventsStream,
  fetchShopCatalog,
  fetchShopVariants,
  fetchShopPreviewData,
  estimateShopShipping,
  uploadShopArtwork,
  createShopOrder,
  createShopCheckout,
  fetchArPhotos,
  fetchMapPhotos,
  fetchContributors,
  fetchPosterPool,
  createPoster,
  fetchPosters,
  uploadMapPhoto,
  fetchAdminSession,
  loginAdmin,
  logoutAdmin,
} from './services/apiClient.js';
import { createGeolocationService } from './services/geolocationService.js';
import { createCarteMapFeature } from './features/map/carteMap.js';
import { prepareAnimLine, computeLineGeometry, drawLine, drawLineInCtx } from './features/animation/lineEngine.js';
import { createExportService } from './features/export/exportService.js';
import { createPunchlineAnimationFeature } from './features/animation/punchlineAnimation.js';
import { createRealtimeDashboardFeature } from './features/dashboard/realtimeDashboard.js';
import { createDashboardCore } from './features/dashboard/dashboardCore.js';
import { createDashboardCardFeature } from './features/dashboard/dashboardCard.js';
import { createArPhotoDashboardFeature } from './features/dashboard/arPhotoDashboard.js';
import { createMapPhotoDashboardFeature } from './features/dashboard/mapPhotoDashboard.js';
import { createSpherePhotoDashboardFeature } from './features/dashboard/spherePhotoDashboard.js';
import { createPosterDashboardFeature } from './features/dashboard/posterDashboard.js';
import { createContributorsDashboardFeature } from './features/dashboard/contributorsDashboard.js';
import { createDashboardTabsFeature } from './features/dashboard/dashboardTabs.js';
import { createSphereSettingsDashboardFeature } from './features/dashboard/sphereSettingsDashboard.js';
import { createAlphabetsDashboardFeature } from './features/dashboard/alphabetsDashboard.js';
import { createGalerieFeature } from './features/galerie/galerieFeature.js';
import { createExportButtonsFeature } from './features/export/exportButtons.js';
import { createTabNavigationFeature } from './features/ui/tabNavigation.js';
import { createAppBootstrapFeature } from './features/bootstrap/appBootstrap.js';
import { createProposerFeature } from './features/proposer/proposerFeature.js';
import { createMapPhotoUploadFeature } from './features/map/mapPhotoUpload.js';
import { initHeaderMenu } from './features/ui/headerMenu.js';
import { initGalerieFullscreen } from './features/ui/galerieFullscreen.js';
import { initGalerieShuffleToggle } from './features/ui/galerieShuffleToggle.js';
import { escapeHtml } from './utils/stringUtils.js';
import { createNotificationToggle } from './features/dashboard/notificationToggle.js';
import { getCachedIsAdmin, setCachedIsAdmin } from './services/adminUiCache.js';
import { resolveLineData } from './utils/knockoutRecord.js';

// ── DOM refs ───────────────────────────────────────────────────────────────────
const input                = document.getElementById('phraseInput');
const pseudoInput          = document.getElementById('pseudoInput');
const pseudoRow            = pseudoInput.closest('.pseudo-row');
const generateBtn          = document.getElementById('generateBtn');
const preview              = document.getElementById('preview');
const previewFooter        = document.getElementById('previewFooter');
const animPreviewFooter    = document.getElementById('animPreviewFooter');
const punchlineAnimPreview = document.getElementById('punchlineAnimPreview');
const status               = document.getElementById('status');
const navLinks             = document.querySelectorAll('.nav-link[data-tab]');
const animPreviewFull      = document.getElementById('anim-preview-full');
const galerieAnimFull      = document.getElementById('galerie-anim-full');
const galerieAnimPreview   = document.getElementById('galerieAnimPreview');
const dashboardList        = document.getElementById('dashboardList');
const dashboardEmpty       = document.getElementById('dashboardEmpty');
const dashboardCount       = document.getElementById('dashboardCount');
const headerEl             = document.querySelector('header');
const headerLogo           = document.getElementById('headerLogo');
const burgerBtn            = document.getElementById('burger-btn');
const hoverTrigger         = document.getElementById('galerie-hover-trigger');
const fullscreenBtn        = document.getElementById('galerie-fullscreen-btn');
const shuffleBtn           = document.getElementById('galerie-shuffle-btn');
const iconExpand           = document.getElementById('icon-expand');
const iconCompress         = document.getElementById('icon-compress');
const dashboardNavLinks    = Array.from(document.querySelectorAll('.nav-link[data-tab="dashboard"]'));
const shopDryRunRow        = document.getElementById('shopDryRunRow');
const adminLogoutBtn       = document.getElementById('adminLogoutBtn');
const adminLoginModal      = document.getElementById('adminLoginModal');
const adminLoginForm       = document.getElementById('adminLoginForm');
const adminLoginPassword   = document.getElementById('adminLoginPassword');
const adminLoginError      = document.getElementById('adminLoginError');
const adminLoginCancel     = document.getElementById('adminLoginCancel');
const adminLoginSubmit     = document.getElementById('adminLoginSubmit');

// ── État global ────────────────────────────────────────────────────────────────
let fontBuffer = null;
let lastSvg    = null;
let lastName   = 'output.svg';
let animFont   = null;
const dashboardItems = [];
let arPhotos   = [];
let mapPhotos  = [];
let dbLoaded   = false;

// Références en avant pour les features à dépendances croisées
let galerieFeature;
let tabNavigation;
let exportButtons;
let realtimeDashboard;
let isAdminUser = false;

function stripAdminLoginQuery() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('admin_login')) return;
  url.searchParams.delete('admin_login');
  url.searchParams.delete('next');
  const nextPath = `${url.pathname}${url.searchParams.toString() ? `?${url.searchParams.toString()}` : ''}${url.hash}`;
  history.replaceState(history.state, '', nextPath);
}

function resolvePostLoginRedirectPath() {
  const url = new URL(window.location.href);
  const nextParam = String(url.searchParams.get('next') || '').trim();
  if (nextParam.startsWith('/')) return nextParam;
  return '/dashboard';
}

function applyAdminUiAccess(isAdmin) {
  dashboardNavLinks.forEach(function(link) {
    link.hidden = !isAdmin;
  });
  if (adminLogoutBtn) {
    adminLogoutBtn.hidden = !isAdmin;
  }
  if (shopDryRunRow) {
    shopDryRunRow.hidden = !isAdmin;
  }
}

function setAdminLoginError(message) {
  if (!adminLoginError) return;
  if (!message) {
    adminLoginError.hidden = true;
    adminLoginError.textContent = '';
    return;
  }
  adminLoginError.textContent = message;
  adminLoginError.hidden = false;
}

function setAdminLoginBusy(isBusy) {
  if (adminLoginPassword) adminLoginPassword.disabled = isBusy;
  if (adminLoginCancel) adminLoginCancel.disabled = isBusy;
  if (adminLoginSubmit) adminLoginSubmit.disabled = isBusy;
}

function requestAdminAccessViaModal() {
  if (!adminLoginModal || !adminLoginForm || !adminLoginPassword) {
    return Promise.resolve(false);
  }

  return new Promise(function(resolve) {
    let done = false;

    function cleanup(result) {
      if (done) return;
      done = true;
      adminLoginModal.hidden = true;
      adminLoginForm.removeEventListener('submit', onSubmit);
      adminLoginCancel?.removeEventListener('click', onCancel);
      adminLoginModal.removeEventListener('click', onBackdropClick);
      window.removeEventListener('keydown', onKeyDown);
      resolve(result);
    }

    async function onSubmit(event) {
      event.preventDefault();
      const password = String(adminLoginPassword.value || '');
      if (!password) {
        setAdminLoginError('Mot de passe requis.');
        adminLoginPassword.focus();
        return;
      }

      setAdminLoginBusy(true);
      setAdminLoginError('');
      try {
        await loginAdmin(password);
        cleanup(true);
      } catch {
        setAdminLoginBusy(false);
        setAdminLoginError('Mot de passe invalide.');
        adminLoginPassword.focus();
        adminLoginPassword.select();
      }
    }

    function onCancel() {
      cleanup(false);
    }

    function onBackdropClick(event) {
      if (event.target === adminLoginModal) {
        cleanup(false);
      }
    }

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        cleanup(false);
      }
    }

    setAdminLoginBusy(false);
    setAdminLoginError('');
    adminLoginPassword.value = '';
    adminLoginModal.hidden = false;
    adminLoginForm.addEventListener('submit', onSubmit);
    adminLoginCancel?.addEventListener('click', onCancel);
    adminLoginModal.addEventListener('click', onBackdropClick);
    window.addEventListener('keydown', onKeyDown);
    adminLoginPassword.focus();
  });
}

async function resolveAdminUiAccess() {
  // Affichage optimiste depuis le dernier statut admin connu, le temps que
  // la vraie vérification serveur réponde — évite que Dashboard/Déconnecter
  // clignotent à chaque chargement de page pour l'admin.
  applyAdminUiAccess(getCachedIsAdmin());

  try {
    const session = await fetchAdminSession();
    isAdminUser = Boolean(session?.isAdmin);
  } catch {
    isAdminUser = false;
  }

  applyAdminUiAccess(isAdminUser);
  setCachedIsAdmin(isAdminUser);

  const url = new URL(window.location.href);
  if (!url.searchParams.has('admin_login')) return;
  if (isAdminUser) {
    stripAdminLoginQuery();
    return;
  }

  const granted = await requestAdminAccessViaModal();
  if (!granted) {
    if (window.location.pathname === '/admin-login') {
      window.location.href = '/';
      return;
    }
    stripAdminLoginQuery();
    return;
  }

  isAdminUser = true;
  applyAdminUiAccess(isAdminUser);
  window.location.href = resolvePostLoginRedirectPath();
}

const adminAccessReady = resolveAdminUiAccess();

// ── Chargement police ──────────────────────────────────────────────────────────
async function loadFont() {
  if (fontBuffer) return fontBuffer;
  status.textContent = 'Chargement de la police…';
  const res = await fetch('./assets/fonts/nocode.otf');
  if (!res.ok) throw new Error('Impossible de charger assets/fonts/nocode.otf');
  fontBuffer = await res.arrayBuffer();
  status.textContent = '';
  return fontBuffer;
}

// Fix iOS Safari : le clavier virtuel scrolle le visualViewport — on recentre aussitôt
window.visualViewport?.addEventListener('scroll', () => window.scrollTo(0, 0));

// ── Fonctions de câblage (déclarées tôt, utilisées via let) ───────────────────
function rebuildGalerie() { galerieFeature.rebuild(); }

async function addToDashboard(text, pseudo = '', lat = null, lng = null, knockout = null) {
  await realtimeDashboard.addToDashboard(text, pseudo, lat, lng, knockout);
}

function connectSSE() { realtimeDashboard.connectSSE(); }

// ── Services ───────────────────────────────────────────────────────────────────
const geolocationService = createGeolocationService();
const exportService      = createExportService({ drawLine, drawLineInCtx });

// ── Carte Leaflet ──────────────────────────────────────────────────────────────
const carteMapFeature = createCarteMapFeature({
  loadFont,
  generateSVG,
  escapeHtml,
  getDashboardItems: () => dashboardItems,
  getArPhotos:       () => arPhotos,
  getMapPhotos:      () => mapPhotos,
});

async function updateCarteMarkers() {
  try { arPhotos  = await fetchArPhotos();  } catch (e) { console.error('AR photos fetch:', e); }
  try { mapPhotos = await fetchMapPhotos(); } catch (e) { console.error('Map photos fetch:', e); }
  await carteMapFeature.updateMarkers();
  carteMapFeature.updatePhotoMarkers();
  carteMapFeature.updateMapPhotoMarkers();
  carteMapFeature.fitBoundsToMarkers();
}

// ── Animation punchline ────────────────────────────────────────────────────────
const punchlineAnim = createPunchlineAnimationFeature({
  punchlineAnimPreview,
  drawLine,
  loadFont,
  parseFont,
  prepareAnimLine,
  getAnimFont: () => animFont,
  setAnimFont: (font) => { animFont = font; },
});

// ── Proposer ───────────────────────────────────────────────────────────────────
const proposerFeature = createProposerFeature({
  input,
  pseudoInput,
  pseudoRow,
  generateBtn,
  preview,
  previewFooter,
  animPreviewFooter,
  status,
  loadFont,
  generateSVG,
  normalizeFilename,
  renderPunchlineAnimation: (text) => punchlineAnim.render(text),
  renderPunchlineAnimationFromData: (lineData) => punchlineAnim.renderFromData(lineData),
  getDashboardItems:        () => dashboardItems,
  resyncDeletions:          () => realtimeDashboard.resyncDeletions(),
  geolocationService,
  addToDashboard,
  setLastOutput: (svg, filename) => { lastSvg = svg; lastName = filename; },
});

const showHint = proposerFeature.showHint;
const renderAll = proposerFeature.renderAll;
proposerFeature.bindEvents();

// ── Dashboard core ─────────────────────────────────────────────────────────────
const dashboardCore = createDashboardCore({
  dashboardItems,
  dashboardCount,
  dashboardEmpty,
  drawLine,
  animSpeed: ANIM_SPEED,
});

const { updateDashCount, resizeDashMiniCanvas, stopAllDashAnims, stopDashMiniAnim, startDashMiniAnim } = dashboardCore;

// ── Dashboard card ─────────────────────────────────────────────────────────────
const dashboardCard = createDashboardCardFeature({
  dashboardItems,
  dashboardList,
  exportService,
  normalizeFilename,
  loadFont,
  generateSVG,
  parseFont,
  prepareAnimLine,
  getAnimFont:       () => animFont,
  setAnimFont:       (font) => { animFont = font; },
  resizeDashMiniCanvas,
  startDashMiniAnim,
  stopDashMiniAnim,
  updateDashCount,
  rebuildGalerie,
  updateCarteMarkers,
});

const createDashCard = dashboardCard.createDashCard;

// Cartes du dashboard construites à la première ouverture de l'onglet
// (réservé aux admins) plutôt qu'au chargement pour tout le monde : ~32
// éléments par punchline (3 700 pour 116 punchlines, 85 % de la page), plus
// un canvas chacune, pour une liste que les visiteurs ne voient jamais.
let dashboardCardsWanted = false;

function ensureDashboardCards() {
  if (!dashboardCardsWanted) return;
  const fresh = dashboardItems.filter((item) => !item.cardEl);
  if (fresh.length === 0) return;
  fresh.forEach((item) => createDashCard(item));
  // Remet toutes les cartes dans l'ordre de dashboardItems (nouvelles
  // comprises) -- appendChild déplace celles déjà présentes.
  const fragment = document.createDocumentFragment();
  dashboardItems.forEach((item) => fragment.appendChild(item.cardEl));
  dashboardList.appendChild(fragment);
  fresh.forEach((item) => {
    resizeDashMiniCanvas(item);
    if (typeof ResizeObserver !== 'undefined' && item.canvas) {
      item.ro = new ResizeObserver(() => resizeDashMiniCanvas(item));
      item.ro.observe(item.canvas.parentElement);
    }
  });
}

// L'animation des mini-aperçus ne doit tourner que si la liste est à
// l'écran : avant, chaque punchline reçue en temps réel la relançait, même
// depuis la galerie, qui partageait alors le temps d'image avec le
// redessin de toutes les cartes masquées.
function startDashMiniAnimIfVisible(item) {
  if (tabNavigation?.getCurrentTab() === 'dashboard' && dashboardTabsFeature.getDashTab() === 'punchlines') {
    startDashMiniAnim(item);
  }
}

// ── Galerie ────────────────────────────────────────────────────────────────────
galerieFeature = createGalerieFeature({
  dashboardItems,
  galerieAnimPreview,
  galerieAnimFull,
  galerieEmptyEl:    document.getElementById('galerieEmpty'),
  drawLine,
  animSpeed:         ANIM_SPEED,
  isDbLoaded:        () => dbLoaded,
  isGalerieTabActive: () => tabNavigation?.getCurrentTab() === 'galerie',
  onRebuildDone:     () => { exportButtons?.rebuildVertical(); exportButtons?.rebuildHorizontal(); },
});

// Bouton "Commander" d'Escales : /shop?poster=12. Lu dès le chargement : le
// démarrage de l'appli réécrit ensuite l'URL sans ses paramètres, avant que
// le shop (chargé à la demande) soit prêt (même souci que pendingPhotoKey
// dans carteMap.js).
const initialShopPosterId = window.location.pathname === '/shop'
  ? Number(new URLSearchParams(window.location.search).get('poster')) || null
  : null;

// ── Shop (chargé à la demande) ───────────────────────────────────────────────────
// shopFeature.js pèse à lui seul ~2500 lignes (catalogue Printful, panier,
// checkout Stripe) pour un onglet qu'une minorité de visiteurs ouvre : on ne
// le télécharge qu'à la première visite de l'onglet Shop plutôt que de
// l'inclure dans le bundle principal.
let shopFeature = null;
let shopFeatureLoading = null;
function ensureShopFeature() {
  if (shopFeature) return Promise.resolve(shopFeature);
  if (!shopFeatureLoading) {
    shopFeatureLoading = import('./features/shop/shopFeature.js').then(({ createShopFeature }) => {
      shopFeature = createShopFeature({
        isAdmin: () => isAdminUser,
        getDashboardItems: function() { return dashboardItems; },
        fetchShopCatalog,
        fetchShopVariants,
        fetchShopPreviewData,
        estimateShopShipping,
        uploadShopArtwork,
        createShopOrder,
        createShopCheckout,
        generateSVG,
        getFontBuffer: loadFont,
        fetchPosters,
        initialPosterId: initialShopPosterId,
      });
      return shopFeature;
    });
  }
  return shopFeatureLoading;
}

// ── Posters (chargé à la demande, comme le Shop) ─────────────────────────────────
let postersFeature = null;
let postersFeatureLoading = null;
function ensurePostersFeature() {
  if (postersFeature) return Promise.resolve(postersFeature);
  if (!postersFeatureLoading) {
    postersFeatureLoading = import('./features/posters/postersFeature.js').then(({ createPostersFeature }) => {
      postersFeature = createPostersFeature({
        fetchPosterPool,
        createPoster,
        computeLineGeometry,
        getFont: async () => parseFont(await loadFont()),
      });
      return postersFeature;
    });
  }
  return postersFeatureLoading;
}

// ── UI : menu burger & fullscreen galerie ──────────────────────────────────────
initHeaderMenu({ headerEl, burgerBtn, navLinks });

// Le logo est maintenant un SVG statique embarqué dans le HTML (plus de
// génération async au chargement, cf. suppression de renderHeaderLogo) :
// il ne reste qu'à câbler le clic pour revenir à la galerie.
headerLogo?.addEventListener('click', () => tabNavigation.switchTab('galerie'));

if (adminLogoutBtn) {
  adminLogoutBtn.addEventListener('click', async function() {
    adminLogoutBtn.disabled = true;
    try {
      await logoutAdmin();
    } catch {
      // no-op: même en cas d'erreur, on force un retour public
    }
    window.location.href = '/';
  });
}

// initGalerieFullscreen avant tabNavigation.bindEvents() : exitGalerieFullscreen
// doit s'exécuter avant switchTab pour éviter un flash de layout.
initGalerieFullscreen({
  hoverTrigger,
  fullscreenBtn,
  extraButtons:   [shuffleBtn],
  iconExpand,
  iconCompress,
  headerEl,
  galerieSection: document.getElementById('sec-galerie'),
  navLinks,
  getCurrentTab:  () => tabNavigation.getCurrentTab(),
});

initGalerieShuffleToggle({ shuffleBtn, galerieFeature });

// ── Navigation par onglets ─────────────────────────────────────────────────────
tabNavigation = createTabNavigationFeature({
  navLinks,
  mainEl: document.querySelector('main'),
  sections: {
    proposer:  document.getElementById('sec-proposer'),
    galerie:   document.getElementById('sec-galerie'),
    dashboard: document.getElementById('sec-dashboard'),
    posters:   document.getElementById('sec-posters'),
    shop:      document.getElementById('sec-shop'),
    carte:     document.getElementById('sec-carte'),
  },
  animPreviewFull,
  galerieAnimFull,
  galerieEmptyEl:     document.getElementById('galerieEmpty'),
  mapEl:              document.getElementById('carte-map'),
  hasDashboardItems:  () => dashboardItems.length > 0,
  isDbLoaded:         () => dbLoaded,
  invalidateCarteMap: () => carteMapFeature.invalidateSize(),
  canAccessDashboard: () => isAdminUser,
  onTabEffects: function(opts) {
    const tab = opts.tab;
    const hasItems = opts.hasItems;
    const applyCarteViewportLayout = opts.applyCarteViewportLayout;
    const scheduleCarteViewportLayout = opts.scheduleCarteViewportLayout;
    if (tab !== 'shop') {
      shopFeature?.closePunchlineModal?.();
    }
    if (tab === 'galerie') {
      if (hasItems) {
        galerieFeature.rebuild();
      }
      stopAllDashAnims();
      punchlineAnim.stop();
    } else if (tab === 'dashboard') {
      galerieFeature.stop();
      dashboardCardsWanted = true;
      ensureDashboardCards();
      // Ne redémarre l'animation de la liste que si c'est bien le
      // sous-onglet "Punchlines" qui est affiché -- sinon (retour sur
      // Dashboard depuis un autre onglet top-niveau, ex. Galerie, alors
      // qu'un autre sous-onglet comme Alphabets était resté ouvert), elle
      // tournerait pour rien en arrière-plan (cf. dashboardTabsFeature,
      // qui gère déjà l'arrêt/redémarrage sur un changement de sous-onglet).
      if (dashboardTabsFeature.getDashTab() === 'punchlines' && dashboardItems.length > 0) {
        dashboardItems.forEach(item => resizeDashMiniCanvas(item));
        startDashMiniAnim(null);
      }
      punchlineAnim.stop();
    } else if (tab === 'posters') {
      galerieFeature.stop();
      stopAllDashAnims();
      punchlineAnim.stop();
      ensurePostersFeature().then((feature) => feature.onEnter());
    } else if (tab === 'shop') {
      galerieFeature.stop();
      stopAllDashAnims();
      punchlineAnim.stop();
      ensureShopFeature().then((feature) => feature.onEnterShop());
    } else if (tab === 'carte') {
      galerieFeature.stop();
      stopAllDashAnims();
      punchlineAnim.stop();
      setTimeout(async function() {
        applyCarteViewportLayout(false);
        // initMap() peut retenter en interne (conteneur pas encore
        // dimensionné) : on attend qu'elle soit réellement prête avant de
        // charger les marqueurs, sinon updateCarteMarkers() s'exécute dans
        // le vide (carteMap encore undefined) et ne se relance jamais.
        await carteMapFeature.initMap();
        updateCarteMarkers();
        setTimeout(scheduleCarteViewportLayout, 300);
      }, 60);
    } else {
      galerieFeature.stop();
      stopAllDashAnims();
      if (punchlineAnim.getLine()) punchlineAnim.start();
    }
  },
});

tabNavigation.bindEvents();

// ── Boutons d'export ───────────────────────────────────────────────────────────
exportButtons = createExportButtonsFeature({
  exportService,
  galerieFeature,
  getPunchlineAnimLine: () => punchlineAnim.getLine(),
  getLastOutput:        () => ({ svg: lastSvg, name: lastName }),
  input,
  normalizeFilename,
  loadFont,
  generateSVG,
});
exportButtons.bindProposerExports();

// ── Upload photo carte ─────────────────────────────────────────────────────────
createMapPhotoUploadFeature({
  geolocationService,
  uploadMapPhoto,
  onUploadSuccess: updateCarteMarkers,
});

// ── Dashboard : panneau photos AR ─────────────────────────────────────────────
const arPhotoDashboard = createArPhotoDashboardFeature({
  fetchArPhotos,
});

// ── Dashboard : panneau photos Carte ──────────────────────────────────────────
const mapPhotoDashboard = createMapPhotoDashboardFeature({
  fetchMapPhotos,
});

// ── Dashboard : panneau photos Sphère (page Sillages) ─────────────────────────
const spherePhotoDashboard = createSpherePhotoDashboardFeature();
const posterDashboard = createPosterDashboardFeature();

// ── Dashboard : panneau contributeurs ──────────────────────────────────────────
const contributorsDashboard = createContributorsDashboardFeature({ fetchContributors });

// ── Dashboard : panneau sphère des alphabets (réglage sillage) ─────────────────
const sphereSettingsDashboard = createSphereSettingsDashboardFeature();

// ── Dashboard : panneau vitrine des alphabets ───────────────────────────────────
const alphabetsDashboard = createAlphabetsDashboardFeature();

// ── Dashboard : sous-onglets ───────────────────────────────────────────────────
const dashboardTabsFeature = createDashboardTabsFeature({
  onArTab:  () => arPhotoDashboard.load(),
  onMapTab: () => mapPhotoDashboard.load(),
  onSpherePhotosTab: () => spherePhotoDashboard.load(),
  onPostersTab:      () => posterDashboard.load(),
  onContributorsTab: () => contributorsDashboard.load(),
  onSphereTab: () => sphereSettingsDashboard.load(),
  onAlphabetsTab: () => alphabetsDashboard.load(),
  // La boucle d'animation des mini-aperçus de la liste (voir onTabEffects
  // plus bas) tournait en continu dès l'entrée dans le dashboard, même sur
  // un autre sous-onglet (Alphabets, Sphère...) où elle ne sert à rien mais
  // continue de redessiner chaque punchline masquée à chaque frame -- ça
  // rendait les animations propres à ces sous-onglets saccadées.
  startPunchlinesAnim: () => {
    if (dashboardItems.length > 0) {
      dashboardItems.forEach((item) => resizeDashMiniCanvas(item));
      startDashMiniAnim(null);
    }
  },
  stopPunchlinesAnim: stopAllDashAnims,
});

// ── Dashboard : toggle notifications email ────────────────────────────────────
createNotificationToggle({ btn: document.getElementById('dash-notif-btn') });

// ── Dashboard temps réel (SSE) ─────────────────────────────────────────────────
realtimeDashboard = createRealtimeDashboardFeature({
  dashboardItems,
  dashboardList,
  createEventsStream,
  createGenerationRecord,
  prepareAnimLine,
  resolveLineData,
  loadFont,
  parseFont,
  getAnimFont:       () => animFont,
  setAnimFont:       (font) => { animFont = font; },
  createDashCard:    (item) => (dashboardCardsWanted ? createDashCard(item) : null),
  updateDashCount,
  resizeDashMiniCanvas,
  startDashMiniAnim: startDashMiniAnimIfVisible,
  stopDashMiniAnim,
  rebuildGalerie,
  updateCarteMarkers,
  refreshContributors: () => contributorsDashboard.refreshIfVisible(),
  showHint,
  fetchGenerations,
});

// ── Bootstrap ──────────────────────────────────────────────────────────────────
const appBootstrap = createAppBootstrapFeature({
  fetchGenerations,
  loadFont,
  parseFont,
  prepareAnimLine,
  resolveLineData,
  getAnimFont:              () => animFont,
  setAnimFont:              (font) => { animFont = font; },
  dashboardItems,
  updateDashCount,
  onItemsLoaded:            ensureDashboardCards,
  rebuildGalerie,
  renderAll,
  connectSSE,
  tabFromPath:              tabNavigation.tabFromPath,
  switchTab:                tabNavigation.switchTab,
  getPunchlineAnimAspectRatio: () => punchlineAnim.getAspectRatio(),
  setPunchlineAnimAspectRatio: (r) => punchlineAnim.setAspectRatio(r),
  resizePunchlineAnimCanvas:   () => punchlineAnim.resize(),
  punchlineAnimPreview,
  waitForAdminAccess:          () => adminAccessReady,
  markDbLoaded:                () => { dbLoaded = true; },
});

appBootstrap.bindLoad();
